import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * FeatureTour — first-run spotlight tour.
 *
 * Highlights key platform features for first-time users: each step pins a
 * glowing ring on a real UI element (via a CSS selector) and pops a small
 * card next to it with a one-line explainer. No full-screen dim overlay, so
 * it works regardless of the app's stacking contexts.
 *
 * Accessibility: the card is a labelled dialog, Esc / "Skip tour" dismisses,
 * focus moves into the card on each step and returns to the invoker on close,
 * and the step counter is announced via a polite live region.
 */

export interface TourStep {
  /** CSS selector of the element to highlight. Steps with no match are skipped. */
  target: string;
  title: string;
  body: string;
}

const SEEN_KEY = 'gn-feature-tour-seen-v1';

/**
 * The "seen" flag is keyed per user id so a second account on the same
 * browser (shared computer, QA runs) still gets the first-run offer.
 * Falls back to the unkeyed flag for contexts without a user.
 */
const seenKey = (userId?: string) => (userId ? `${SEEN_KEY}:${userId}` : SEEN_KEY);

export function hasSeenTour(userId?: string): boolean {
  try {
    return localStorage.getItem(seenKey(userId)) === '1';
  } catch {
    return false;
  }
}

export function markTourSeen(userId?: string): void {
  try {
    localStorage.setItem(seenKey(userId), '1');
  } catch {
    /* storage unavailable — tour just shows again next time */
  }
}

export function resetTourSeen(userId?: string): void {
  try {
    localStorage.removeItem(seenKey(userId));
  } catch {
    /* noop */
  }
}

const CARD_WIDTH = 320;
const MARGIN = 12;

export default function FeatureTour({
  steps,
  open,
  onClose,
  userId,
}: {
  steps: TourStep[];
  open: boolean;
  onClose: () => void;
  /** Optional user id — keys the "seen" flag per account. */
  userId?: string;
}) {
  const [idx, setIdx] = useState(0);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const invokerRef = useRef<Element | null>(null);
  const titleId = 'feature-tour-title';

  const close = useCallback(() => {
    document.querySelectorAll('.tour-spotlight').forEach((el) => el.classList.remove('tour-spotlight'));
    markTourSeen(userId);
    onClose();
    // Return focus to whatever opened the tour.
    const invoker = invokerRef.current;
    if (invoker instanceof HTMLElement) invoker.focus();
  }, [onClose, userId]);

  // Reset to the first step whenever the tour opens.
  useEffect(() => {
    if (open) {
      invokerRef.current = document.activeElement;
      setIdx(0);
    }
  }, [open ]);

  // Position the card + spotlight for the current step. Steps whose target
  // isn't on screen (different track, missing nav item) are skipped.
  useEffect(() => {
    if (!open) return;
    document.querySelectorAll('.tour-spotlight').forEach((el) => el.classList.remove('tour-spotlight'));

    let i = idx;
    let el: Element | null = null;
    while (i < steps.length) {
      el = document.querySelector(steps[i].target);
      if (el) break;
      i += 1;
    }
    if (!el || i >= steps.length) {
      close();
      return;
    }
    if (i !== idx) setIdx(i);

    el.classList.add('tour-spotlight');
    el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });

    const place = () => {
      const r = el!.getBoundingClientRect();
      const cardH = cardRef.current?.offsetHeight ?? 220;
      const below = r.bottom + MARGIN + cardH <= window.innerHeight;
      const top = below ? r.bottom + MARGIN : Math.max(MARGIN, r.top - MARGIN - cardH);
      let left = Math.min(
        Math.max(MARGIN, r.left + r.width / 2 - CARD_WIDTH / 2),
        window.innerWidth - CARD_WIDTH - MARGIN,
      );
      if (left < MARGIN) left = MARGIN;
      setPos({ top: Math.max(MARGIN, top), left });
    };
    // Place after paint so the card has a measured height.
    const raf = requestAnimationFrame(place);
    window.addEventListener('resize', place);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', place);
      el?.classList.remove('tour-spotlight');
    };
  }, [open, idx, steps, close]);

  // Focus the card on each step so keyboard + screen-reader users land on it.
  useEffect(() => {
    if (open && pos) cardRef.current?.focus();
  }, [open, pos, idx]);

  // Esc dismisses.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  if (!open) return null;
  const step = steps[idx];
  if (!step) return null;
  const last = idx === steps.length - 1;

  return (
    <div className="tour-layer" aria-hidden={false}>
      <div
        ref={cardRef}
        className="tour-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        style={pos ? { top: pos.top, left: pos.left, width: CARD_WIDTH } : { visibility: 'hidden' }}
      >
        <p className="tour-step-count" aria-live="polite">
          {idx + 1} of {steps.length}
        </p>
        <h3 id={titleId} className="tour-title">{step.title}</h3>
        <p className="tour-body">{step.body}</p>
        <div className="tour-actions">
          <button
            type="button"
            className="tour-skip"
            onClick={close}
          >
            Skip tour
          </button>
          <span className="tour-nav">
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              disabled={idx === 0}
              onClick={() => setIdx((v) => Math.max(0, v - 1))}
            >
              ← Back
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => (last ? close() : setIdx((v) => v + 1))}
            >
              {last ? 'Finish' : 'Next →'}
            </button>
          </span>
        </div>
      </div>
    </div>
  );
}
